import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import SEO from '../SEO';
import { BBCodeRenderer, renderFormattedBBCode } from '../components/BBCodeRenderer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', name: 'All Discussions', icon: '🌐', bg: 'bg-amber-50', text: 'text-amber-900', border: 'border-amber-300' },
  { id: 'iyer-iyengar-rituals', name: 'Iyer-Iyengar Rituals', icon: '🔥', bg: 'bg-orange-50', text: 'text-orange-900', border: 'border-orange-300' },
  { id: 'dharma-shastram', name: 'Dharma Sastram', icon: '📚', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300' },
  { id: 'bhakthi-pooja-sthothrams', name: 'Bhakthi, Pooja & Sthothrams', icon: '🙏', bg: 'bg-purple-50', text: 'text-purple-900', border: 'border-purple-300' },
  { id: 'panchangam', name: 'Panchangam & Dates', icon: '📅', bg: 'bg-blue-50', text: 'text-blue-900', border: 'border-blue-300' },
  { id: 'religious-festivals', name: 'Religious & Monthly Festivals', icon: '🪔', bg: 'bg-emerald-50', text: 'text-emerald-900', border: 'border-emerald-300' },
  { id: 'matrimonials-ads', name: 'FREE Matrimonials & Ads', icon: '💍', bg: 'bg-pink-50', text: 'text-pink-900', border: 'border-pink-300' },
  { id: 'general-social', name: 'General & Social Discussions', icon: '💬', bg: 'bg-indigo-50', text: 'text-indigo-900', border: 'border-indigo-300' },
];

export const ForumPage = () => {
  const { user, language } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Active Thread Details State
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAuthor, setReplyAuthor] = useState(user ? user.name : '');
  const [replySubmitting, setReplySubmitting] = useState(false);

  // New Thread Modal State
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('General & Social Discussions');
  const [authorName, setAuthorName] = useState(user ? user.name : '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [currentPage, selectedCategory, searchQuery]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let url = `${API}/forum/posts?page=${currentPage}&limit=15`;
      if (selectedCategory && selectedCategory !== 'all') {
        const catObj = CATEGORIES.find(c => c.id === selectedCategory);
        if (catObj) url += `&category=${encodeURIComponent(catObj.name)}`;
      }
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await axios.get(url);
      setPosts(res.data.items || []);
      setTotalPages(res.data.pages || 1);
      setTotalCount(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch forum posts', e);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleThreadClick = async (nodeId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/forum/post/${nodeId}`);
      setSelectedThread(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert('Could not load discussion thread.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    try {
      setSubmitting(true);
      await axios.post(`${API}/forum/create`, {
        title: newTitle,
        content: newContent,
        category: newCategory,
        author_name: authorName || 'Devotee'
      });
      setShowModal(false);
      setNewTitle('');
      setNewContent('');
      fetchPosts();
      alert('Discussion topic created successfully!');
    } catch (e) {
      alert('Failed to create topic.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedThread) return;

    try {
      setReplySubmitting(true);
      const targetId = selectedThread.vb_nodeid || selectedThread.id;
      await axios.post(`${API}/forum/reply/${targetId}`, {
        content: replyText,
        author_name: replyAuthor || 'Devotee'
      });
      setReplyText('');
      // Reload thread
      handleThreadClick(targetId);
    } catch (e) {
      alert('Failed to post reply.');
    } finally {
      setReplySubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EE] text-[#1B5E20] py-6 px-3 sm:px-6">
      <SEO
        title="Community Forum — Ahobilam.com"
        description="Authentic Sri Vaishnava & Vaideeka discussion forum. Explore 37,000+ topics on Sastram, Rituals, Panchangam, Bhakthi, and Matrimonials."
      />

      <div className="max-w-6xl mx-auto">
        {/* Top View Switcher Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <button
            className="px-4 py-2.5 bg-[#CC6600] text-white font-bold text-xs sm:text-sm rounded-xl shadow-md flex items-center gap-2"
          >
            🌐 Community Forum (37,300+ Discussions)
          </button>
          <Link
            to="/qa"
            className="px-4 py-2.5 bg-white text-[#CC6600] font-bold text-xs sm:text-sm rounded-xl border border-[#D4AF37]/40 shadow-xs hover:bg-[#FAF6EE] flex items-center gap-2"
          >
            ❓ Ask Question / Q&A Board
          </Link>
          <a
            href="/panchangam-showcase.html"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md hover:scale-105 transition-all flex items-center gap-2 border border-amber-300 animate-pulse"
          >
            ✨ 🌟 Tamil Panchangam 2026-2027 Showcase (Must Watch!)
          </a>
        </div>
        {/* Banner Header */}
        <div className="bg-gradient-to-r from-[#FAF6EE] via-[#FDF8ED] to-[#F5EDDA] border-2 border-[#D4AF37]/50 rounded-2xl p-4 sm:p-6 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <span className="text-xs uppercase font-bold tracking-wider text-[#CC6600] bg-[#FAF6EE] px-3 py-1 rounded-full border border-[#D4AF37]/40 inline-block mb-2">
              🪔 Traditional Community Forum
            </span>
            <h1 className="text-xl sm:text-3xl font-extrabold text-[#1A237E] tracking-tight">
              {language === 'ta' ? 'அகோபிலம் ஆன்மீக விவாத மன்றம்' : 'Ahobilam Community Discussion Board'}
            </h1>
            <p className="text-xs sm:text-sm text-[#2D5A1E] mt-1 font-medium">
              37,400+ authentic Sastra topics & discussions preserved since 2000.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-[#CC6600] to-[#E65100] hover:from-[#E65100] hover:to-[#CC6600] text-white font-bold rounded-xl shadow-md transition-transform transform active:scale-95 text-xs sm:text-sm flex items-center justify-center gap-2"
          >
            <span>➕</span> {language === 'ta' ? 'புதிய விவாதம் தொடங்க' : 'Start New Discussion'}
          </button>
        </div>

        {/* BrahminsNet Account Migration Prominent Banner */}
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 rounded-2xl p-4 sm:p-5 text-white shadow-lg border-2 border-amber-300 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl sm:text-4xl bg-white/20 p-2.5 rounded-xl">🔑</span>
            <div>
              <h3 className="font-extrabold text-sm sm:text-base tracking-wide text-amber-50">
                {language === 'ta'
                  ? 'பழைய brahminsnet.com மன்ற பயனர்கள் உங்கள் பயனர் பெயர் அல்லது மின்னஞ்சல் மற்றும் கடவுச்சொல்லுடன் உள்நுழையலாம்'
                  : 'Old brahminsnet.com forums user can log in with your own user name or email and password!'}
              </h3>
              <p className="text-xs text-amber-100 mt-0.5 font-medium">
                {language === 'ta'
                  ? 'உங்கள் பழைய BrahminsNet கணக்கு விவரங்கள் அகோபிலம் மன்றத்தில் முழுமையாக இணைக்கப்பட்டுள்ளன.'
                  : 'All your previous BrahminsNet forum profile, threads, and credentials are standard & ready.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              const event = new CustomEvent('open-auth-modal');
              window.dispatchEvent(event);
            }}
            className="w-full sm:w-auto px-5 py-2.5 bg-white hover:bg-amber-50 text-[#CC6600] font-black rounded-xl shadow-md transition-all text-xs sm:text-sm whitespace-nowrap cursor-pointer hover:scale-105"
          >
            🔑 Log In / Sign In
          </button>
        </div>

        {/* Selected Thread View */}
        {selectedThread ? (
          <div className="bg-white rounded-2xl border border-[#D4AF37]/40 p-4 sm:p-6 shadow-sm mb-6 animate-fade-in">
            <button
              onClick={() => setSelectedThread(null)}
              className="mb-4 px-3 py-1.5 bg-[#F5EDDA] text-[#1A237E] text-xs font-bold rounded-lg hover:bg-[#FDF8ED] flex items-center gap-1 border border-[#D4AF37]/30"
            >
              ⬅ Back to All Discussions
            </button>

            <div className="border-b border-gray-100 pb-4 mb-4">
              <span className="text-xs font-bold bg-[#FAF6EE] text-[#CC6600] px-2.5 py-1 rounded-full border border-[#D4AF37]/30">
                {selectedThread.category || 'General'}
              </span>
              <h2 className="text-lg sm:text-2xl font-bold text-[#1A237E] mt-3 leading-snug">
                {selectedThread.title}
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                <span>👤 Posted by <strong className="text-[#1B5E20]">{selectedThread.author_name || 'Devotee'}</strong></span>
                <span>📅 {formatDate(selectedThread.created_at)}</span>
                <span>👁 {selectedThread.views || 1} views</span>
              </div>
            </div>

            {/* Thread Body */}
            <div className="bg-[#FAF6EE]/50 p-4 rounded-xl border border-[#F5EDDA] mb-6">
              <BBCodeRenderer content={selectedThread.content} />
            </div>

            {/* Replies List */}
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h3 className="text-base font-bold text-[#1A237E] mb-4 flex items-center gap-2">
                💬 Replies ({selectedThread.replies ? selectedThread.replies.length : 0})
              </h3>

              {selectedThread.replies && selectedThread.replies.length > 0 ? (
                <div className="space-y-4 mb-6">
                  {selectedThread.replies.map((reply, idx) => (
                    <div key={idx} className="p-3 sm:p-4 rounded-xl bg-white border border-[#D4AF37]/30 shadow-2xs">
                      <div className="flex justify-between items-center text-xs mb-2">
                        <span className="font-bold text-[#1A237E]">👤 {reply.author_name || 'Devotee'}</span>
                        <span className="text-gray-400">{formatDate(reply.created_at)}</span>
                      </div>
                      <BBCodeRenderer content={reply.content} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-6 italic">No replies yet. Be the first to post a reply!</p>
              )}

              {/* Reply Form */}
              <form onSubmit={handleCreateReply} className="bg-[#FAF6EE] p-4 rounded-xl border border-[#D4AF37]/30">
                <h4 className="text-xs font-bold text-[#1A237E] mb-3">Post a Reply</h4>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Your Name (Optional)"
                    value={replyAuthor}
                    onChange={(e) => setReplyAuthor(e.target.value)}
                    className="w-full text-xs p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:border-[#CC6600]"
                  />
                </div>
                <div className="mb-3">
                  <textarea
                    rows="3"
                    placeholder="Write your response..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    required
                    className="w-full text-xs sm:text-sm p-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[#CC6600]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={replySubmitting}
                  className="px-4 py-2 bg-[#1B5E20] hover:bg-[#2D5A1E] text-white font-bold text-xs rounded-lg transition-colors disabled:opacity-50"
                >
                  {replySubmitting ? 'Posting...' : 'Submit Reply'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          /* Forum Overview Main Grid */
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Category Sidebar */}
            <div className="lg:col-span-1 space-y-2">
              <h3 className="text-xs uppercase font-extrabold tracking-wider text-[#1A237E] mb-2 px-1">
                Forum Categories
              </h3>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left p-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-between ${
                    selectedCategory === cat.id
                      ? 'bg-[#CC6600] text-white border-[#CC6600] shadow-sm'
                      : 'bg-white text-[#2D5A1E] border-[#D4AF37]/30 hover:bg-[#FAF6EE]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    <span className="truncate">{cat.name}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* Main Content List Area */}
            <div className="lg:col-span-3">
              {/* Search Bar */}
              <div className="bg-white p-3 rounded-2xl border border-[#D4AF37]/30 shadow-2xs mb-4 flex gap-2">
                <input
                  type="text"
                  placeholder="Search 37,000+ Sastra discussions, topics, keywords..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="flex-1 text-xs sm:text-sm px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-[#CC6600]"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-600 font-bold"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Total Count Bar */}
              <div className="flex justify-between items-center text-xs text-gray-500 mb-3 px-1">
                <span>Showing {posts.length} of <strong className="text-[#1A237E]">{totalCount.toLocaleString()}</strong> discussions</span>
                <span>Page {currentPage} of {totalPages}</span>
              </div>

              {/* Thread Cards List */}
              {loading ? (
                <div className="py-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#CC6600] border-t-transparent mb-2"></div>
                  <p className="text-xs text-gray-500 font-medium">Loading Sastra discussions...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="bg-white p-8 rounded-2xl border border-[#D4AF37]/30 text-center">
                  <span className="text-3xl block mb-2">🪔</span>
                  <p className="text-sm font-bold text-[#1A237E]">No discussion topics found</p>
                  <p className="text-xs text-gray-500 mt-1">Try clearing your search query or choosing another category.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {posts.map((post, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleThreadClick(post.vb_nodeid || post.id)}
                      className="bg-white p-4 rounded-xl border border-[#D4AF37]/30 hover:border-[#CC6600] hover:shadow-md transition-all cursor-pointer group"
                    >
                      <div className="flex justify-between items-start gap-2 mb-1.5">
                        <span className="text-[10px] font-bold bg-[#FAF6EE] text-[#CC6600] px-2 py-0.5 rounded-full border border-[#D4AF37]/30">
                          {post.category || 'General'}
                        </span>
                        {post.source === 'brahminsnet' && (
                          <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            📜 BrahminsNet
                          </span>
                        )}
                      </div>

                      <h4 className="text-xs sm:text-sm font-bold text-[#1A237E] group-hover:text-[#CC6600] transition-colors leading-snug line-clamp-2">
                        {post.title}
                      </h4>

                      <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                        {post.content ? post.content.replace(/\[[^\]]+\]/g, '').replace(/<[^>]*>?/gm, '').substring(0, 160) : ''}...
                      </p>

                      <div className="flex justify-between items-center text-[11px] text-gray-400 mt-3 pt-2 border-t border-gray-50">
                        <span>👤 {post.author_name || 'Devotee'}</span>
                        <div className="flex items-center gap-3">
                          <span>📅 {formatDate(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#D4AF37]/20">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-[#D4AF37]/40 rounded-xl text-xs font-bold text-[#1A237E] hover:bg-[#FAF6EE] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-xs font-bold text-[#1A237E]">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-[#D4AF37]/40 rounded-xl text-xs font-bold text-[#1A237E] hover:bg-[#FAF6EE] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* New Topic Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 sm:p-6 shadow-2xl border border-[#D4AF37]/40 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
              <h3 className="text-base font-extrabold text-[#1A237E]">Start New Discussion Topic</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateThread} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-[#1A237E] mb-1">Your Name</label>
                <input
                  type="text"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full p-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-[#CC6600]"
                />
              </div>

              <div>
                <label className="block font-bold text-[#1A237E] mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-[#CC6600]"
                >
                  {CATEGORIES.filter((c) => c.id !== 'all').map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-[#1A237E] mb-1">Topic Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Enter discussion title..."
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-[#CC6600]"
                />
              </div>

              <div>
                <label className="block font-bold text-[#1A237E] mb-1">Discussion Content / Question *</label>
                <textarea
                  rows="5"
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  placeholder="Write full details..."
                  required
                  className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#CC6600] text-xs sm:text-sm"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-[#CC6600] hover:bg-[#E65100] text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Publish Topic'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumPage;
